import { OrganisationProductSummary } from "@fero/components";
import { useGetProductQuery, useGetProductSalePricesQuery } from "@fero/hooks";
import type { AvailableStock } from "@fero/pages";
import type { SelectedProduct } from "@fero/stores";
import {
  Box,
  Checkbox,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import type { NumberOfUnits, ProductId } from "adl-gen/ferovinum/app/db";
import type React from "react";
import { AutoSizer, List, type ListRowProps } from "react-virtualized";
import { getVesselCapacity } from "utils/model-utils";
import { useManageBrowseProducts, useTradeSaleHelpers } from "../hooks";

interface BrowseProductsModalTableProps {
  productsToShow: AvailableStock;
  producibles: Map<ProductId, string>;
}

/**
 * Minimum fixed row height required by the virtualized table
 * If table had variable row heights it will require a more complex and performance heavy implementation
 * As we will need to pre-render cells in order to calculate their height
 */
const FIXED_ROW_HEIGHT = 100;

interface BrowseProductsTableHeaderProps {
  showProducible: boolean;
}

const BrowseProductsTableHeader: React.FC<BrowseProductsTableHeaderProps> = (
  props,
) => {
  const { getSalePriceLabel } = useTradeSaleHelpers();
  const { showProducible } = props;
  return (
    <TableHead sx={{ display: "table", tableLayout: "fixed", width: "100%" }}>
      <TableRow>
        <TableCell width={100} />
        <TableCell>Product</TableCell>
        <TableCell align="right" width={200}>
          Available
        </TableCell>
        {showProducible && (
          <TableCell align="right" width={200}>
            Producible
          </TableCell>
        )}
        {/* <TableCell align="right">Producible</TableCell> */}
        <TableCell align="right" width={200}>
          Sale Price {getSalePriceLabel()}
        </TableCell>
      </TableRow>
    </TableHead>
  );
};

interface BrowseProcutsTableRowProps {
  productId: string;
  quantities: NumberOfUnits | undefined;
  isProductSelected: boolean;
  onClick: (product: SelectedProduct) => void;
  width: number;
  style: ListRowProps["style"];
  producibles: Map<ProductId, string>;
}

interface TableCellWithLoadingProps {
  isLoading: boolean;
  isError: boolean;
  value?: string | number;
  align?: "left" | "center" | "right";
  width?: number;
}

const TableCellWithLoading: React.FC<TableCellWithLoadingProps> = ({
  isLoading,
  isError,
  value,
  align,
  width,
  children,
}) => {
  const cellProps = { width, align };
  if (isLoading) {
    return (
      <TableCell {...cellProps}>
        <Box sx={{ display: "flex", flex: 1, justifyContent: align }}>
          <Skeleton
            width={width ? width * 0.8 : 400}
            height={100}
            animation="pulse"
          />
        </Box>
      </TableCell>
    );
  }

  if (isError) {
    return <TableCell {...cellProps}>--</TableCell>;
  }

  return (
    <TableCell {...cellProps}>{value ? <p>{value}</p> : children}</TableCell>
  );
};

const BrowseProductsTableRow: React.FC<BrowseProcutsTableRowProps> = (
  props,
) => {
  const { getSalePrice, nominatedPurchaserId } = useTradeSaleHelpers();
  const {
    productId,
    quantities,
    isProductSelected,
    onClick,
    width,
    style,
    producibles,
  } = props;
  const {
    data: summary,
    isLoading: isLoadingProduct,
    isError: isErrorProduct,
  } = useGetProductQuery({ productId });

  const {
    data: productSalePrices,
    isLoading: isLoadingProductSalePrices,
    isError: isErrorGetSalePrice,
  } = useGetProductSalePricesQuery({
    nominatedPurchaserId,
    enabled: !!nominatedPurchaserId,
  });

  const producibleQuantity = producibles.get(productId);

  const productSalePrice = productSalePrices?.find(
    (p) => p.productCode === summary?.value.code,
  );
  const onSelectProduct = () => {
    const producible = Number.parseInt(producibleQuantity ?? "-", 10);
    if (summary && quantities) {
      const availableQuantity = quantities?.value ?? 0;
      const isCaskProduct = summary.value.unitType === "cask";
      const initialQuantity = isCaskProduct ? availableQuantity : 0;
      const salePrice = getSalePrice(productSalePrice) ?? 0;
      const initialSubtotal = initialQuantity * salePrice;

      onClick({
        productId,
        summary,
        available: availableQuantity,
        producible: Number.isSafeInteger(producible) ? producible : 0,
        subtotal: initialSubtotal,
        quantity: initialQuantity,
        salePrice,
        lineItemDiscount: 0,
        freeUnits: 0,
        attributeSelection: {},
      });
    }
    // Do nothing?
  };
  return (
    <TableRow
      key={productId}
      onClick={onSelectProduct}
      sx={{
        bgcolor: isProductSelected ? "#F5F4FA80" : undefined,
        display: "table",
        tableLayout: "fixed",
        width,
        height: FIXED_ROW_HEIGHT,
      }}
      style={style}
    >
      <TableCell width={100}>
        <Checkbox checked={isProductSelected} onChange={onSelectProduct} />
      </TableCell>
      <TableCellWithLoading
        isLoading={isLoadingProduct}
        isError={isErrorProduct}
      >
        {summary ? (
          <OrganisationProductSummary
            code={summary.value.code}
            name={summary.value.name}
            producerName={summary.value.producerName}
            productDate={summary.value.productDate}
            unitType={summary.value.unitType}
            vesselCapacity={getVesselCapacity(summary.value.vesselSize)}
          />
        ) : (
          <p>-</p>
        )}
      </TableCellWithLoading>
      <TableCell align="right" width={200}>
        {quantities
          ? summary?.value.unitType === "cask"
            ? `1 cask (${quantities.value} LPA)`
            : quantities.value
          : "-"}
      </TableCell>
      {producibles.size !== 0 && (
        <TableCellWithLoading
          value={producibleQuantity}
          align="right"
          width={200}
          isLoading={false}
          isError={false}
        />
      )}
      <TableCellWithLoading
        isLoading={isLoadingProductSalePrices}
        isError={isErrorGetSalePrice}
        value={
          productSalePrice && getSalePrice(productSalePrice)
            ? `${productSalePrice.currency} ${getSalePrice(productSalePrice)}`
            : "-"
        }
        align="right"
        width={200}
      />
    </TableRow>
  );
};

export const BrowseProductsModalTable: React.FC<
  BrowseProductsModalTableProps
> = (props) => {
  const { productsToShow, producibles } = props;
  const { toggleBrowsingProduct, browsingProducts } = useManageBrowseProducts();

  if (productsToShow.size === 0) {
    return (
      <TableContainer component={Paper} elevation={0}>
        <Table>
          <BrowseProductsTableHeader showProducible={producibles.size !== 0} />
          <TableBody>
            <TableRow>
              <TableCell colSpan={4} align="center">
                No products found
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }
  const productIds = Array.from(productsToShow.keys());
  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        flex: 1,
        display: "flex",
      }}
    >
      <AutoSizer>
        {({ width, height }) => (
          <Table>
            <BrowseProductsTableHeader
              showProducible={producibles.size !== 0}
            />
            <TableBody sx={{ flex: 1, display: "flex", width: "100%" }}>
              <List
                autoContainerWidth
                height={height - 100}
                rowCount={productIds.length}
                width={width}
                rowHeight={FIXED_ROW_HEIGHT}
                rowRenderer={({ index, style }) => {
                  const productId = productIds[index];
                  const isProductSelected =
                    browsingProducts.find((p) => p.productId === productId) !==
                    undefined;
                  return (
                    <BrowseProductsTableRow
                      key={productId}
                      quantities={productsToShow.get(productId)}
                      productId={productId}
                      isProductSelected={isProductSelected}
                      onClick={(product) =>
                        toggleBrowsingProduct(product, !isProductSelected)
                      }
                      width={width}
                      style={style}
                      producibles={producibles}
                    />
                  );
                }}
              />
            </TableBody>
          </Table>
        )}
      </AutoSizer>
    </TableContainer>
  );
};
